import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { Type } from '../../models/Type'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { okResult } from '../../types/utils'
import { Step, navigateToNextDefaultStep } from '../../utils/defaults'
import { getEditionID } from '../../utils/users'
import { PSApplication } from '../app/PSApplication'
import { TypesPage } from '../items/TypesPage'
import { WorkflowSelectListSkeleton } from '../common/skeletons/WorkflowSelectListSkeleton'

export function SetupDefaultFormsPage() {
    const me = PSApplication.me()
    const owner = PSApplication.owner()
    const editionID = getEditionID(me)
    const navigate = usePSNavigate()
    const [searchParams] = useSearchParams()

    const { logAndDisplayError } = useErrorHandler()

    const { findLastHistoryEntry } = useHistoryTrackerContext()
    const lastHistoryEntry = findLastHistoryEntry({ excluding: /^\/settings\/.*\/.+/ })

    const queryClient = useQueryClient()
    const { data, isLoading } = useQuery({
        queryKey: ['defaults', 'forms', editionID],
        queryFn: async () => {
            const recordIDs = searchParams.get('recordIDs')?.split(',') ?? []
            const [edition, installedForms] = await Promise.all([
                RecordFactory.getInstance().loadEdition(getEditionID(me)),
                RecordFactory.getInstance().loadTypes(owner.id!),
            ])

            const installedFormsFromSameEdition = installedForms.filter(
                (f) => f.edition === editionID
            )

            const selectableForms = (edition?.defaultTypes ?? [])
                .filter((f) => (recordIDs.length > 0 ? recordIDs.includes(f.id ?? '') : true))
                .filter(
                    (f) =>
                        installedFormsFromSameEdition.find((i) => i.editionType === f.id) ===
                        undefined
                )
                .map((f) => ({ form: f, selected: false }))

            return {
                edition,
                forms: selectableForms,
                allowNextWithoutSelection: recordIDs.length > 0,
            }
        },
    })

    const onNextHandler = useCallback(
        async (forms: Type[]) => {
            // Send the new items with the proper IDs set
            const clonedForms = forms.map((form) => {
                const clone = form.clone()
                clone.edition = editionID
                clone.editionType = form.id
                clone.fields = form.fields.map((field) => {
                    const cf = field.clone()
                    cf.editionField = field.id
                    cf.id = undefined
                    return cf
                })
                clone.id = undefined

                return clone
            })

            const result = await RecordFactory.getInstance().sendOnboardingTypes(
                clonedForms,
                editionID,
                false
            )
            if (!result.ok) {
                logAndDisplayError(result.error)
                return result
            }

            queryClient.invalidateQueries({
                queryKey: ['defaults'],
            })

            const initialStep = (searchParams.get('initialStep') ?? 'forms') as Step
            const potentialItemIDs = searchParams.get('recordIDs')?.split(',') ?? []

            await navigateToNextDefaultStep(navigate, {
                edition: data?.edition!,
                potentialItemIDs,
                step: 'forms',
                initialStep,
            })

            return okResult(null)
        },
        [navigate, data]
    )

    if (isLoading || !data) {
        return (
            <WorkflowSelectListSkeleton
                title="Forms"
                subtitle="Attach forms to your offerings to automatically send when clients book, when
                    applicable."
                onClose={() => {}}
            />
        )
    }

    return (
        <TypesPage
            onClose={() => {
                if (lastHistoryEntry) {
                    lastHistoryEntry.goBackTo()
                }
                else {
                    navigate('/')
                }
            }}
            onBack={{
                title: 'Forms',
                onClick: () => navigate('/settings/forms'),
            }}
            forms={data.forms}
            onSave={onNextHandler}
            allowNextWithoutSelection={data.allowNextWithoutSelection}
            edition={data.edition}
        />
    )
}
